import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { numberWithCommas, toTitleCase, versionFormat } from "../utils.js";
import { fetchStageInfo } from "../data/stages.js";
import { statusIsActive } from "../graphComponents/reuseTimeline.js";
import CompareChartPanel from "./BoosterCompareCharts.js";

const MAX_COMPARE = 4;
const COMPARE_COLORS = ["#238bb8", "#8067d8", "#d6a84a", "#168f7d"];

const formatEnum = (value, fallback = "—") =>
  toTitleCase(String(value || fallback).replace(/_/g, " ")).replace("Tbd", "TBD");

const formatDays = (days) => {
  if (!Number.isFinite(days)) {
    return "—";
  }
  if (days < 10) {
    return `${days.toFixed(1)}d`;
  }
  return `${Math.round(days)}d`;
};

const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.valueOf())) {
    return "—";
  }
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatYears = (days) => {
  if (!Number.isFinite(days) || days <= 0) {
    return "—";
  }
  const years = days / 365.25;
  return years < 1 ? `${Math.round(days)}d` : `${years.toFixed(1)}y`;
};

const formatPayloadMass = (kg) => {
  if (!Number.isFinite(kg) || kg <= 0) {
    return "—";
  }
  if (kg >= 1000) {
    const tonnes = kg / 1000;
    const amount = tonnes >= 100 ? numberWithCommas(Math.round(tonnes)) : tonnes.toFixed(1);
    return `${amount} tonnes`;
  }
  return `${numberWithCommas(Math.round(kg))} kg`;
};

const getStatusClass = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (statusIsActive(normalized)) {
    return "active";
  }
  if (normalized.includes("RETIRED")) {
    return "retired";
  }
  if (normalized.includes("EXPENDED")) {
    return "expended";
  }
  if (normalized.includes("LOST")) {
    return "lost";
  }
  return "unknown";
};

const parseMassKg = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const landingBucket = (method) => {
  if (method === "DRONE_SHIP") {
    return "asds";
  }
  if (method === "GROUND_PAD") {
    return "rtls";
  }
  if (method === "CATCH") {
    return "catch";
  }
  if (method === "EXPENDED") {
    return "expended";
  }
  return "other";
};

const extractComparisonProfile = (stage, recoveries = []) => {
  const now = Date.now();
  const past = (recoveries || [])
    .map((reuse) => {
      const time = reuse.launch?.time ? new Date(reuse.launch.time) : null;
      if (!time || Number.isNaN(time.valueOf()) || time.valueOf() > now) {
        return null;
      }
      const turnaroundSeconds = Number(reuse.stage_turnaround);
      const method = String(reuse.method || "").toUpperCase();
      const methodSuccess = String(reuse.method_success || "").toUpperCase();
      return {
        time,
        turnaroundDays: Number.isFinite(turnaroundSeconds) && turnaroundSeconds > 0 ? turnaroundSeconds / 86400 : null,
        method,
        methodSuccess,
        massKg: parseMassKg(reuse.launch?.mass),
        launchName: reuse.launch?.name || "",
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time)
    .map((flight, index) => ({ ...flight, flightNumber: index + 1 }));

  const MS_PER_DAY = 86400000;
  const flights = past.length || Number(stage?.num_launches || 0);
  const firstFlight = past[0]?.time || null;
  const latestFlight = past[past.length - 1]?.time || null;
  // Date arithmetic is in milliseconds — convert with ms/day, not seconds/day.
  const careerDays =
    firstFlight && latestFlight
      ? Math.max(0, Math.round((latestFlight.valueOf() - firstFlight.valueOf()) / MS_PER_DAY))
      : null;
  const daysSinceLast =
    latestFlight && !Number.isNaN(latestFlight.valueOf())
      ? Math.max(0, Math.round((now - latestFlight.valueOf()) / MS_PER_DAY))
      : null;

  const turnarounds = past.map((flight) => flight.turnaroundDays).filter((days) => Number.isFinite(days) && days > 0);
  const fastestTurnaround = turnarounds.length ? Math.min(...turnarounds) : null;
  const avgTurnaround = turnarounds.length
    ? turnarounds.reduce((sum, days) => sum + days, 0) / turnarounds.length
    : null;
  const longestLayoff = turnarounds.length ? Math.max(...turnarounds) : null;

  const masses = past.map((flight) => flight.massKg).filter((mass) => Number.isFinite(mass));
  const payloadMassKg = masses.length ? masses.reduce((sum, mass) => sum + mass, 0) : null;
  const payloadFlights = masses.length;
  const avgPayloadKg = payloadFlights ? payloadMassKg / payloadFlights : null;
  const heaviestPayloadKg = masses.length ? Math.max(...masses) : null;

  const landingMix = { asds: 0, rtls: 0, catch: 0, expended: 0, other: 0 };
  past.forEach((flight) => {
    landingMix[landingBucket(flight.method)] += 1;
  });

  const landingAttempts = past.filter((flight) =>
    ["DRONE_SHIP", "GROUND_PAD", "CATCH"].includes(flight.method)
  );
  const landingSuccesses = landingAttempts.filter((flight) => flight.methodSuccess === "SUCCESS").length;
  const landingSuccessRate = landingAttempts.length ? landingSuccesses / landingAttempts.length : null;

  const flightsByYear = {};
  past.forEach((flight) => {
    const year = flight.time.getUTCFullYear();
    flightsByYear[year] = (flightsByYear[year] || 0) + 1;
  });
  const busiestYearEntry = Object.entries(flightsByYear).sort((a, b) => Number(b[1]) - Number(a[1]))[0];
  const busiestYear = busiestYearEntry ? Number(busiestYearEntry[0]) : null;
  const busiestYearFlights = busiestYearEntry ? Number(busiestYearEntry[1]) : null;

  const asds = landingMix.asds;
  const rtls = landingMix.rtls;
  const catches = landingMix.catch;

  const flightsPerYear =
    careerDays && careerDays > 0 && flights > 1 ? flights / (careerDays / 365.25) : null;

  return {
    id: stage.id,
    name: stage.name,
    nickname: stage.nickname || "",
    version: stage.version || "",
    status: stage.status || "",
    flights,
    firstFlight,
    latestFlight,
    careerDays,
    daysSinceLast,
    fastestTurnaround,
    avgTurnaround,
    longestLayoff,
    payloadMassKg,
    payloadFlights,
    avgPayloadKg,
    heaviestPayloadKg,
    asds,
    rtls,
    catches,
    landingMix,
    landingSuccessRate,
    flightsPerYear,
    flightsByYear,
    busiestYear,
    busiestYearFlights,
    flightsLog: past,
  };
};

const METRIC_ROWS = [
  {
    key: "flights",
    label: "Flights",
    get: (p) => p.flights,
    format: (v) => (v ? `x${v}` : "—"),
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "payloadMassKg",
    label: "Total payload lofted",
    get: (p) => p.payloadMassKg,
    format: (v) => formatPayloadMass(v),
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "avgPayloadKg",
    label: "Avg payload per flight",
    get: (p) => p.avgPayloadKg,
    format: (v) => formatPayloadMass(v),
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "flightsPerYear",
    label: "Flights / year",
    get: (p) => p.flightsPerYear,
    format: (v) => (Number.isFinite(v) ? v.toFixed(1) : "—"),
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "fastestTurnaround",
    label: "Fastest turnaround",
    get: (p) => p.fastestTurnaround,
    format: formatDays,
    higherIsBetter: false,
    bar: true,
  },
  {
    key: "avgTurnaround",
    label: "Avg turnaround",
    get: (p) => p.avgTurnaround,
    format: formatDays,
    higherIsBetter: false,
    bar: true,
  },
  {
    key: "careerDays",
    label: "Career span",
    get: (p) => p.careerDays,
    format: formatYears,
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "daysSinceLast",
    label: "Days since last flight",
    get: (p) => p.daysSinceLast,
    format: (v) => (Number.isFinite(v) ? `${numberWithCommas(v)}d` : "—"),
    higherIsBetter: false,
    bar: false,
  },
  {
    key: "firstFlight",
    label: "First flight",
    get: (p) => p.firstFlight?.valueOf?.() ?? null,
    format: (_v, p) => formatDate(p.firstFlight),
    // Earliest debut date
    higherIsBetter: false,
    bar: false,
  },
  {
    key: "latestFlight",
    label: "Latest flight",
    get: (p) => p.latestFlight?.valueOf?.() ?? null,
    format: (_v, p) => formatDate(p.latestFlight),
    // Most recent flight date
    higherIsBetter: true,
    bar: false,
  },
  {
    key: "asds",
    label: "ASDS landings",
    get: (p) => p.asds,
    format: (v) => String(v ?? 0),
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "rtls",
    label: "RTLS landings",
    get: (p) => p.rtls,
    format: (v) => String(v ?? 0),
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "catches",
    label: "Catch attempts",
    get: (p) => p.catches,
    format: (v) => String(v ?? 0),
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "heaviestPayloadKg",
    label: "Heaviest payload",
    get: (p) => p.heaviestPayloadKg,
    format: (v) => formatPayloadMass(v),
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "busiestYearFlights",
    label: "Busiest year",
    get: (p) => p.busiestYearFlights,
    format: (v, p) => (Number.isFinite(v) ? `${p.busiestYear} · x${v}` : "—"),
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "longestLayoff",
    label: "Longest layoff",
    get: (p) => p.longestLayoff,
    format: formatDays,
    higherIsBetter: false,
    bar: true,
  },
  {
    key: "landingSuccessRate",
    label: "Landing success",
    get: (p) => p.landingSuccessRate,
    format: (v) => (Number.isFinite(v) ? `${Math.round(v * 100)}%` : "—"),
    higherIsBetter: true,
    bar: true,
  },
  {
    key: "version",
    label: "Version",
    get: () => null,
    format: (_v, p) => (p.version ? versionFormat(p.version) : "—"),
    higherIsBetter: null,
    bar: false,
  },
  {
    key: "status",
    label: "Status",
    get: () => null,
    format: (_v, p) => formatEnum(p.status),
    higherIsBetter: null,
    bar: false,
  },
];

export const pickDefaultBoosters = (fleet) =>
  [...fleet]
    .filter((stage) => Number(stage.num_launches || 0) > 0)
    .sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0))
    .slice(0, 2)
    .map((stage) => String(stage.id));

const sameId = (a, b) => String(a) === String(b);

/**
 * Bar fill 10–100%. For lower-is-better metrics the smallest value fills most.
 */
const getMetricBarWidth = (raw, values, higherIsBetter) => {
  if (!Number.isFinite(raw) || !values.length) {
    return 0;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);

  if (max <= min) {
    return 100;
  }

  const range = max - min;
  // Map onto 10–100 so the worst entry still shows a short track
  if (higherIsBetter === false) {
    return 10 + ((max - raw) / range) * 90;
  }

  return 10 + ((raw - min) / range) * 90;
};

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "retired", label: "Retired" },
];

/**
 * Full fleet browser: search + status filter + scrollable list.
 * Used when filling/replacing a comparison slot.
 */
const BoosterBrowser = ({ fleet, selectedIds, replaceId, onPick, onClose }) => {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const options = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const selected = new Set(selectedIds.map(String));
    // When replacing, allow the current slot id to stay visible as selected state but not double-add
    if (replaceId) {
      selected.delete(String(replaceId));
    }

    return [...fleet]
      .filter((stage) => !selected.has(String(stage.id)))
      .filter((stage) => {
        const status = String(stage.status || "").toUpperCase();
        if (statusFilter === "active") {
          return statusIsActive(status);
        }
        if (statusFilter === "retired") {
          return status.includes("RETIRED") || status.includes("EXPENDED") || status.includes("LOST");
        }
        return true;
      })
      .filter((stage) => {
        if (!needle) {
          return true;
        }
        const haystack = `${stage.name || ""} ${stage.nickname || ""} ${stage.version || ""}`.toLowerCase();
        return haystack.includes(needle);
      })
      .sort((a, b) => {
        const flightDelta = Number(b.num_launches || 0) - Number(a.num_launches || 0);
        if (flightDelta) {
          return flightDelta;
        }
        return String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true });
      });
  }, [fleet, query, selectedIds, replaceId, statusFilter]);

  return (
    <div className="bc-browser" role="dialog" aria-label="Choose a booster">
      <div className="bc-browser-head">
        <div>
          <p className="bc-browser-kicker">{replaceId ? "Replace booster" : "Add booster"}</p>
          <strong>{options.length} available</strong>
        </div>
        <button type="button" className="bc-text-button" onClick={onClose}>
          Cancel
        </button>
      </div>

      <div className="bc-browser-tools">
        <label className="bc-browser-search">
          <span className="sr-only">Search boosters</span>
          <input
            ref={inputRef}
            type="search"
            value={query}
            placeholder="Search B1067, nickname, version…"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && options[0]) {
                event.preventDefault();
                onPick(options[0].id);
              }
            }}
          />
        </label>
        <div className="bc-browser-filters" role="group" aria-label="Status filter">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={statusFilter === filter.id ? "active" : ""}
              onClick={() => setStatusFilter(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bc-browser-list">
        {options.length ? (
          options.map((stage) => (
            <button
              key={stage.id}
              type="button"
              className="bc-browser-row"
              onClick={() => onPick(stage.id)}
            >
              <span className="bc-browser-row-main">
                <strong>{stage.name}</strong>
                {stage.nickname ? <em>{stage.nickname}</em> : null}
              </span>
              <span className="bc-browser-row-meta">
                <span className={`bc-status ${getStatusClass(stage.status)}`}>{formatEnum(stage.status)}</span>
                <span>{stage.version ? versionFormat(stage.version) : "—"}</span>
                <span className="bc-browser-flights">x{stage.num_launches || 0}</span>
              </span>
            </button>
          ))
        ) : (
          <div className="bc-browser-empty">
            <strong>No boosters match</strong>
            <span>Try another search or status filter.</span>
          </div>
        )}
      </div>
    </div>
  );
};

const BoosterCompare = ({
  fleet = [],
  loading = false,
  selectedIds: selectedIdsProp,
  onSelectedIdsChange,
}) => {
  const isControlled = Array.isArray(selectedIdsProp) && typeof onSelectedIdsChange === "function";
  const [internalSelectedIds, setInternalSelectedIds] = useState([]);
  const selectedIds = isControlled ? selectedIdsProp : internalSelectedIds;

  const [profiles, setProfiles] = useState({});
  const [loadingIds, setLoadingIds] = useState(() => new Set());
  const [errorById, setErrorById] = useState({});
  const [pickerMode, setPickerMode] = useState(null); // null | 'add' | { replace: id }
  const seededRef = useRef(false);
  const cacheRef = useRef({});
  const inflightRef = useRef(new Set());

  // Uncontrolled mode only: seed top flyers once fleet loads
  useEffect(() => {
    if (isControlled || seededRef.current || !fleet.length) {
      return;
    }
    seededRef.current = true;
    setInternalSelectedIds(pickDefaultBoosters(fleet));
  }, [fleet, isControlled]);

  const loadProfile = useCallback(async (rawId) => {
    const id = String(rawId);
    if (cacheRef.current[id]) {
      setProfiles((current) => ({ ...current, [id]: cacheRef.current[id] }));
      return;
    }
    if (inflightRef.current.has(id)) {
      return;
    }

    inflightRef.current.add(id);
    setLoadingIds((current) => new Set(current).add(id));
    setErrorById((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });

    try {
      const detail = await fetchStageInfo(id);
      const profile = extractComparisonProfile(detail.stage || {}, detail.stage_and_recoveries || []);
      const normalized = { ...profile, id };
      cacheRef.current[id] = normalized;
      setProfiles((current) => ({ ...current, [id]: normalized }));
    } catch (error) {
      setErrorById((current) => ({ ...current, [id]: "Could not load booster" }));
    } finally {
      inflightRef.current.delete(id);
      setLoadingIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    selectedIds.forEach((id) => {
      loadProfile(id);
    });
  }, [selectedIds, loadProfile]);

  const closePicker = useCallback(() => setPickerMode(null), []);

  const updateSelectedIds = useCallback(
    (updater) => {
      if (isControlled) {
        onSelectedIdsChange((current) =>
          typeof updater === "function" ? updater(current) : updater
        );
      } else {
        setInternalSelectedIds(updater);
      }
    },
    [isControlled, onSelectedIdsChange]
  );

  const handlePick = useCallback(
    (id) => {
      const normalized = String(id);
      updateSelectedIds((current) => {
        if (pickerMode && typeof pickerMode === "object" && pickerMode.replace) {
          return current.map((item) => (sameId(item, pickerMode.replace) ? normalized : item));
        }
        if (current.some((item) => sameId(item, normalized)) || current.length >= MAX_COMPARE) {
          return current;
        }
        return [...current, normalized];
      });
      setPickerMode(null);
    },
    [pickerMode, updateSelectedIds]
  );

  const removeBooster = useCallback(
    (id) => {
      updateSelectedIds((current) => current.filter((item) => !sameId(item, id)));
    },
    [updateSelectedIds]
  );

  const clearAll = useCallback(() => {
    updateSelectedIds([]);
    setPickerMode(null);
  }, [updateSelectedIds]);

  const orderedProfiles = useMemo(
    () =>
      selectedIds.map((id, index) => ({
        id: String(id),
        color: COMPARE_COLORS[index % COMPARE_COLORS.length],
        profile: profiles[String(id)] || null,
        loading: loadingIds.has(String(id)),
        error: errorById[String(id)] || null,
        fleetMeta: fleet.find((stage) => sameId(stage.id, id)) || null,
      })),
    [selectedIds, profiles, loadingIds, errorById, fleet]
  );

  const readyProfiles = orderedProfiles.filter((item) => item.profile);

  // Set of winning booster ids per metric (supports ties, e.g. two 20d turnarounds)
  const metricWinners = useMemo(() => {
    const winners = {};
    METRIC_ROWS.forEach((row) => {
      if (row.higherIsBetter === null) {
        return;
      }
      const values = readyProfiles
        .map((item) => ({ id: item.id, value: row.get(item.profile) }))
        .filter((item) => Number.isFinite(item.value));
      if (!values.length) {
        return;
      }
      const bestValue = values.reduce((best, item) => {
        if (best === null) {
          return item.value;
        }
        if (row.higherIsBetter) {
          return item.value > best ? item.value : best;
        }
        return item.value < best ? item.value : best;
      }, null);
      winners[row.key] = new Set(
        values.filter((item) => item.value === bestValue).map((item) => item.id)
      );
    });
    return winners;
  }, [readyProfiles]);

  // Card callouts among the current selection (ties allowed)
  const notablesById = useMemo(() => {
    const map = Object.fromEntries(readyProfiles.map((item) => [item.id, []]));
    if (readyProfiles.length < 2) {
      return map;
    }

    const award = (label, pickValue, preferHigher) => {
      const scored = readyProfiles
        .map((item) => ({ id: item.id, value: pickValue(item.profile) }))
        .filter((item) => Number.isFinite(item.value));
      if (!scored.length) {
        return;
      }
      const best = scored.reduce((acc, item) => {
        if (acc === null) {
          return item.value;
        }
        return preferHigher ? Math.max(acc, item.value) : Math.min(acc, item.value);
      }, null);
      scored
        .filter((item) => item.value === best)
        .forEach((item) => {
          map[item.id].push(label);
        });
    };

    // Career / volume
    // Priority order: earlier awards win when we cap badges per card
    award("Most flown", (p) => p.flights, true);
    award("Most payload", (p) => p.payloadMassKg, true);
    award("Oldest", (p) => p.firstFlight?.valueOf?.() ?? null, false);
    award("Youngest", (p) => p.firstFlight?.valueOf?.() ?? null, true);
    award("Highest cadence", (p) => p.flightsPerYear, true);
    award("Fastest turn", (p) => p.fastestTurnaround, false);
    award("Most recent", (p) => p.latestFlight?.valueOf?.() ?? null, true);

    // Keep one clean badge row on every card
    const MAX_BADGES = 2;
    Object.keys(map).forEach((id) => {
      map[id] = map[id].slice(0, MAX_BADGES);
    });

    return map;
  }, [readyProfiles]);

  const insights = useMemo(() => {
    if (!readyProfiles.length) {
      return { cards: [], stories: [] };
    }

    const pickBest = (getValue, preferHigher) => {
      const scored = readyProfiles
        .map((item) => ({ item, value: getValue(item.profile) }))
        .filter((entry) => Number.isFinite(entry.value));
      if (!scored.length) {
        return null;
      }
      return scored.reduce((best, entry) => {
        if (!best) {
          return entry;
        }
        return preferHigher
          ? entry.value > best.value
            ? entry
            : best
          : entry.value < best.value
            ? entry
            : best;
      }, null);
    };

    const combinedFlights = readyProfiles.reduce((sum, item) => sum + Number(item.profile.flights || 0), 0);
    const combinedPayload = readyProfiles.reduce((sum, item) => sum + Number(item.profile.payloadMassKg || 0), 0);
    const fastest = pickBest((profile) => profile.fastestTurnaround, false);
    const cadence = pickBest((profile) => profile.flightsPerYear, true);
    const heaviest = pickBest((profile) => profile.heaviestPayloadKg, true);

    const cards = [
      {
        key: "flights",
        label: readyProfiles.length > 1 ? "Combined flights" : "Flights",
        value: `x${numberWithCommas(combinedFlights)}`,
        note: readyProfiles.length > 1 ? `${readyProfiles.length} selected` : readyProfiles[0].profile.name,
      },
      {
        key: "payload",
        label: readyProfiles.length > 1 ? "Combined payload" : "Payload lofted",
        value: formatPayloadMass(combinedPayload) === "—" ? "—" : formatPayloadMass(combinedPayload),
      },
    ];

    if (fastest) {
      cards.push({
        key: "turn",
        label: "Fastest turnaround",
        value: formatDays(fastest.value),
        note: fastest.item.profile.name,
        color: fastest.item.color,
      });
    } else if (heaviest) {
      cards.push({
        key: "heavy",
        label: "Heaviest payload",
        value: formatPayloadMass(heaviest.value),
        note: heaviest.item.profile.name,
        color: heaviest.item.color,
      });
    }

    if (cadence) {
      cards.push({
        key: "cadence",
        label: "Highest cadence",
        value: `${cadence.value.toFixed(1)} / yr`,
        note: cadence.item.profile.name,
        color: cadence.item.color,
      });
    }

    const stories = [];
    if (readyProfiles.length >= 2) {
      const byFlights = [...readyProfiles].sort((a, b) => b.profile.flights - a.profile.flights);
      const flightGap = byFlights[0].profile.flights - byFlights[1].profile.flights;
      if (flightGap > 0) {
        stories.push({
          text: `${byFlights[0].profile.name} has ${flightGap} more flight${flightGap === 1 ? "" : "s"} than ${byFlights[1].profile.name}.`,
          color: byFlights[0].color,
        });
      }

      const byPayload = [...readyProfiles]
        .filter((item) => Number.isFinite(item.profile.payloadMassKg))
        .sort((a, b) => b.profile.payloadMassKg - a.profile.payloadMassKg);
      if (byPayload.length >= 2) {
        const gap = byPayload[0].profile.payloadMassKg - byPayload[1].profile.payloadMassKg;
        if (gap >= 500) {
          stories.push({
            text: `${byPayload[0].profile.name} has lofted ${formatPayloadMass(gap)} more than ${byPayload[1].profile.name}.`,
            color: byPayload[0].color,
          });
        }
      }

      const byTurn = [...readyProfiles]
        .filter((item) => Number.isFinite(item.profile.avgTurnaround))
        .sort((a, b) => a.profile.avgTurnaround - b.profile.avgTurnaround);
      if (byTurn.length >= 2) {
        const gap = byTurn[1].profile.avgTurnaround - byTurn[0].profile.avgTurnaround;
        if (gap >= 1) {
          stories.push({
            text: `${byTurn[0].profile.name} averages a ${formatDays(gap)} quicker turnaround.`,
            color: byTurn[0].color,
          });
        }
      }

      readyProfiles.forEach((item) => {
        const mix = item.profile.landingMix || {};
        const attempts = mix.asds + mix.rtls + mix.catch;
        if (attempts >= 4) {
          if (mix.rtls / attempts >= 0.35) {
            stories.push({
              text: `${item.profile.name} is an RTLS specialist — ${mix.rtls} ground-pad landings.`,
              color: item.color,
            });
          } else if (mix.asds / attempts >= 0.8) {
            stories.push({
              text: `${item.profile.name} almost always comes home on a drone ship (${mix.asds} ASDS).`,
              color: item.color,
            });
          }
        }
      });
    } else if (readyProfiles[0]?.profile.busiestYear) {
      const profile = readyProfiles[0].profile;
      stories.push({
        text: `Busiest year was ${profile.busiestYear} with x${profile.busiestYearFlights}.`,
        color: readyProfiles[0].color,
      });
    }

    return { cards: cards.slice(0, 4), stories: stories.slice(0, 3) };
  }, [readyProfiles]);

  const visibleMetricRows = useMemo(() => {
    const hasCatches = readyProfiles.some((item) => Number(item.profile.catches) > 0);
    return METRIC_ROWS.filter((row) => row.key !== "catches" || hasCatches);
  }, [readyProfiles]);

  const canAdd = selectedIds.length < MAX_COMPARE;
  const emptySlots = Math.max(0, MAX_COMPARE - selectedIds.length);
  const isPickerOpen = Boolean(pickerMode);

  return (
    <section className="bc-panel bt-panel">
      <div className="bt-panel-head bc-panel-head">
        <div>
          <p className="bt-section-label">Head-to-head</p>
          <h2 className="bt-panel-title">Booster comparison</h2>
          <p className="bt-panel-subnote">
            Pick up to {MAX_COMPARE} boosters. Stats, graphs, and callouts update with the selection.
          </p>
        </div>
        <div className="bc-head-actions">
          <span className="bc-count">
            {selectedIds.length}/{MAX_COMPARE}
          </span>
          {selectedIds.length ? (
            <button type="button" className="bc-text-button" onClick={clearAll}>
              Clear
            </button>
          ) : null}
        </div>
      </div>

      <div className="bc-body">
        {/* Selection slots — large cards + dashed empty tiles */}
        <div className="bc-slots" style={{ "--bc-cols": MAX_COMPARE }} aria-label="Comparison slots">
          {orderedProfiles.map((item) => {
            const name = item.profile?.name || item.fleetMeta?.name || "…";
            const nickname = item.profile?.nickname || item.fleetMeta?.nickname;
            const status = item.profile?.status || item.fleetMeta?.status;
            const notables = notablesById[item.id] || [];
            const isReplacing =
              isPickerOpen && typeof pickerMode === "object" && sameId(pickerMode.replace, item.id);

            return (
              <article
                className={`bc-slot bc-slot-filled ${isReplacing ? "is-replacing" : ""}`}
                key={item.id}
                style={{ "--bc-color": item.color }}
              >
                <header className="bc-slot-head">
                  <div className="bc-column-identity">
                    <span className="bc-swatch" aria-hidden="true" />
                    <div>
                      <Link className="bc-name" to={`/stage/${item.id}/`}>
                        {name}
                      </Link>
                      {nickname ? <span className="bc-nickname">{nickname}</span> : null}
                    </div>
                  </div>
                  <div className="bc-slot-actions">
                    <button
                      type="button"
                      className="bc-slot-action"
                      onClick={() => setPickerMode({ replace: item.id })}
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      className="bc-remove"
                      aria-label={`Remove ${name}`}
                      onClick={() => removeBooster(item.id)}
                    >
                      ×
                    </button>
                  </div>
                </header>

                {item.loading ? (
                  <div className="bc-column-loading">
                    <span className="loading-indicator" />
                    <span>Loading stats…</span>
                  </div>
                ) : item.error ? (
                  <div className="bc-column-error">
                    <strong>{item.error}</strong>
                    <button type="button" className="bc-text-button" onClick={() => loadProfile(item.id)}>
                      Retry
                    </button>
                  </div>
                ) : item.profile ? (
                  <div className="bc-column-meta">
                    <span className={`bc-status ${getStatusClass(status)}`}>{formatEnum(status)}</span>
                    <span className="bc-meta-line">
                      {item.profile.version ? versionFormat(item.profile.version) : "—"} · x{item.profile.flights}
                    </span>
                    <span className="bc-meta-line bc-meta-mass">
                      {Number.isFinite(item.profile.payloadMassKg)
                        ? `${formatPayloadMass(item.profile.payloadMassKg)} total payload`
                        : "Payload unknown"}
                    </span>
                    <div
                      className={`bc-notables ${notables.length ? "" : "is-empty"}`}
                      aria-label={notables.length ? "Notable among selection" : undefined}
                      aria-hidden={notables.length ? undefined : true}
                    >
                      {notables.map((label) => (
                        <span className="bc-notable" key={label}>
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}

          {Array.from({ length: emptySlots }).map((_, index) => {
            const isActiveAdd = isPickerOpen && pickerMode === "add" && index === 0;
            return (
              <button
                key={`empty-${index}`}
                type="button"
                className={`bc-slot bc-slot-empty ${isActiveAdd ? "is-active" : ""} ${
                  index > 0 ? "bc-slot-empty-extra" : ""
                }`}
                disabled={!canAdd || loading}
                onClick={() => setPickerMode("add")}
              >
                <span className="bc-slot-plus" aria-hidden="true">
                  +
                </span>
                <strong>Add booster</strong>
                <span>Search the fleet</span>
              </button>
            );
          })}
        </div>

        {isPickerOpen ? (
          <BoosterBrowser
            fleet={fleet}
            selectedIds={selectedIds}
            replaceId={typeof pickerMode === "object" ? pickerMode.replace : null}
            onPick={handlePick}
            onClose={closePicker}
          />
        ) : null}

        {!selectedIds.length && !isPickerOpen ? (
          <div className="bc-empty">
            <strong>No boosters selected</strong>
            <span>Tap Add booster to browse the fleet and start a comparison.</span>
          </div>
        ) : null}

        {readyProfiles.length >= 1 ? (
          <>
            <div className="bc-insights" aria-label="Selection highlights">
              {insights.cards.map((card) => (
                <article className="bc-insight" key={card.key} style={card.color ? { "--bc-color": card.color } : undefined}>
                  <small>{card.label}</small>
                  <strong>{card.value}</strong>
                  {card.note ? <span>{card.note}</span> : null}
                </article>
              ))}
            </div>

            {insights.stories.length ? (
              <ul className="bc-stories">
                {insights.stories.map((story) => (
                  <li key={story.text} style={{ "--bc-color": story.color }}>
                    {story.text}
                  </li>
                ))}
              </ul>
            ) : null}

            <CompareChartPanel items={orderedProfiles} />

            <div className="bc-metrics-head">
              <p className="bt-section-label">Full scorecard</p>
              <h3 className="bc-charts-title">Side-by-side metrics</h3>
            </div>

            {/* Desktop: side-by-side table */}
            <div className="bc-table-wrap bc-desktop-metrics">
              <table className="bc-table">
                <thead>
                  <tr>
                    <th scope="col">Metric</th>
                    {orderedProfiles.map((item) => (
                      <th key={item.id} scope="col" style={{ "--bc-color": item.color }}>
                        <span className="bc-th-swatch" aria-hidden="true" />
                        {item.profile?.name || item.fleetMeta?.name || "…"}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleMetricRows.map((row) => {
                    const values = readyProfiles
                      .map((item) => row.get(item.profile))
                      .filter((value) => Number.isFinite(value));

                    return (
                      <tr key={row.key}>
                        <th scope="row">{row.label}</th>
                        {orderedProfiles.map((item) => {
                          const profile = item.profile;
                          if (!profile) {
                            return (
                              <td key={item.id}>
                                <span className="bc-cell-muted">{item.loading ? "…" : "—"}</span>
                              </td>
                            );
                          }

                          const raw = row.get(profile);
                          const isWinner = metricWinners[row.key]?.has(item.id);
                          const barWidth =
                            row.bar && Number.isFinite(raw)
                              ? getMetricBarWidth(raw, values, row.higherIsBetter)
                              : 0;

                          return (
                            <td
                              key={item.id}
                              className={isWinner ? "is-winner" : ""}
                              style={{ "--bc-color": item.color }}
                            >
                              <div className="bc-cell">
                                <strong>{row.format(raw, profile)}</strong>
                              </div>
                              {row.bar && Number.isFinite(raw) ? (
                                <span className="bc-bar" aria-hidden="true">
                                  <i style={{ width: `${barWidth}%`, background: item.color }} />
                                </span>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile: full-width stacked metrics (no clipped table) */}
            <div className="bc-mobile-metrics" aria-label="Comparison metrics">
              {visibleMetricRows.map((row) => {
                const values = readyProfiles
                  .map((item) => row.get(item.profile))
                  .filter((value) => Number.isFinite(value));

                return (
                  <section className="bc-mobile-metric" key={row.key}>
                    <h3 className="bc-mobile-metric-title">{row.label}</h3>
                    <div className="bc-mobile-metric-rows">
                      {orderedProfiles.map((item) => {
                        const profile = item.profile;
                        const name = profile?.name || item.fleetMeta?.name || "…";
                        if (!profile) {
                          return (
                            <div className="bc-mobile-metric-row" key={`${row.key}-${item.id}`}>
                              <span className="bc-mobile-metric-name">
                                <i className="bc-swatch" style={{ "--bc-color": item.color }} />
                                {name}
                              </span>
                              <span className="bc-cell-muted">{item.loading ? "…" : "—"}</span>
                            </div>
                          );
                        }

                        const raw = row.get(profile);
                        const isWinner = metricWinners[row.key]?.has(item.id);
                        const barWidth =
                          row.bar && Number.isFinite(raw)
                            ? getMetricBarWidth(raw, values, row.higherIsBetter)
                            : 0;

                        return (
                          <div
                            className={`bc-mobile-metric-row ${isWinner ? "is-winner" : ""}`}
                            key={`${row.key}-${item.id}`}
                            style={{ "--bc-color": item.color }}
                          >
                            <div className="bc-mobile-metric-top">
                              <span className="bc-mobile-metric-name">
                                <i className="bc-swatch" aria-hidden="true" />
                                {name}
                              </span>
                              <strong>{row.format(raw, profile)}</strong>
                            </div>
                            {row.bar && Number.isFinite(raw) ? (
                              <span className="bc-bar" aria-hidden="true">
                                <i style={{ width: `${barWidth}%`, background: item.color }} />
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
};

export default BoosterCompare;
